/**
 * Device fingerprint — generates a stable per-device hash from non-PII browser signals.
 *
 * Signals combined:
 *   - userAgent
 *   - language
 *   - platform
 *   - screen resolution + color depth
 *   - timezone
 *   - hardware concurrency
 *   - canvas rendering hash
 *   - WebGL renderer hash
 *
 * The hash is stable across sessions on the same device/browser, but differs
 * across devices, browsers, and privacy-mode sessions. No IP, cookies, or
 * storage are required. SHA-256 is computed via Web Crypto API.
 */

export async function getDeviceFingerprint(): Promise<string> {
  if (typeof window === "undefined") return ""

  const signals: string[] = []

  try {
    signals.push(navigator.userAgent || "")
    signals.push(navigator.language || "")
    signals.push((navigator as any).platform || "")
    signals.push(String(navigator.hardwareConcurrency || 0))
    signals.push(`${screen.width}x${screen.height}x${screen.colorDepth}`)
    signals.push(Intl.DateTimeFormat().resolvedOptions().timeZone || "")
    signals.push(String((navigator as any).deviceMemory || 0))
    signals.push(String(screen.pixelDepth || 0))
  } catch {}

  // Canvas fingerprint
  try {
    const canvas = document.createElement("canvas")
    canvas.width = 280
    canvas.height = 60
    const ctx = canvas.getContext("2d")
    if (ctx) {
      ctx.textBaseline = "top"
      ctx.font = "16px 'Arial'"
      ctx.fillStyle = "#f60"
      ctx.fillRect(125, 1, 62, 20)
      ctx.fillStyle = "#069"
      ctx.fillText("NairAlpha 🔐 device", 2, 15)
      ctx.fillStyle = "rgba(102, 204, 0, 0.7)"
      ctx.fillText("NairAlpha 🔐 device", 4, 17)
      signals.push(canvas.toDataURL())
    }
  } catch {}

  // WebGL fingerprint
  try {
    const gl = document.createElement("canvas").getContext("webgl") as WebGLRenderingContext | null
    if (gl) {
      const dbg = gl.getExtension("WEBGL_debug_renderer_info")
      if (dbg) {
        signals.push(String(gl.getParameter((dbg as any).UNMASKED_VENDOR_WEBGL) || ""))
        signals.push(String(gl.getParameter((dbg as any).UNMASKED_RENDERER_WEBGL) || ""))
      }
    }
  } catch {}

  const joined = signals.join("|")

  // SHA-256 via Web Crypto
  try {
    const encoder = new TextEncoder()
    const data = encoder.encode(joined)
    const hashBuf = await crypto.subtle.digest("SHA-256", data)
    const hashArray = Array.from(new Uint8Array(hashBuf))
    return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("")
  } catch {
    // Fallback: simple djb2 hash
    let h = 5381
    for (let i = 0; i < joined.length; i++) {
      h = (h * 33) ^ joined.charCodeAt(i)
    }
    return `fb_${(h >>> 0).toString(16)}`
  }
}
